import * as React from 'react';
import { makeStyles, tokens, Title3, Text, Button, Spinner } from '@fluentui/react-components';
import { useDiagnostico } from '../../hooks/useDiagnostico';
import { useProject } from '../../hooks/useProject';
import ProjectSelector from './components/ProjectSelector';
import EventCards from './components/EventCards';
import ExtractionDetails from './ExtractionDetails';
import { DiagnosticoEvento } from '../../types/diagnostico';

const useStyles = makeStyles({
  container: {
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'left',
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalXL}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  title: {
    color: tokens.colorNeutralForeground1,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXL,
    padding: tokens.spacingHorizontalXL,
    margin: '0 auto',
  },
  selectorSection: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusLarge,
    padding: tokens.spacingHorizontalXL,
  },
  resultsSection: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '400px',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '200px',
    gap: tokens.spacingHorizontalS,
  },
  errorCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '200px',
    padding: tokens.spacingHorizontalXXL,
    textAlign: 'center',
    gap: tokens.spacingVerticalL,
  },
});

const Diagnostico: React.FC = () => {
  const styles = useStyles();
  const { 
    diagnostico, 
    diagnosticoLoading, 
    diagnosticoError, 
    fetchDiagnostico, 
    fetchExtracoesEvento, 
    extracoesLoading,
  } = useDiagnostico();
  const { projects, fetchProjects } = useProject();

  const [selectedProjectId, setSelectedProjectId] = React.useState<number | undefined>();
  const [selectedEvent, setSelectedEvent] = React.useState<DiagnosticoEvento | null>(null);
  const [currentMessageIndex, setCurrentMessageIndex] = React.useState(0);
  const [currentDiagnosticoData, setCurrentDiagnosticoData] = React.useState<DiagnosticoEvento[] | null>(null);

  // Carregar lista de projetos ao montar o componente
  React.useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const loadingMessages = [
    "Aventurei-me no abismo das configurações!",
    "Contemple! Estamos à beira do abismo da perfeição!",
    "Estou mergulhando nas profundezas das minúcias intrincadas, explorando cada configuração com fervor!",
    "Contemple! Estamos à beira do abismo da perfeição!"
  ];

  const selectedProject = projects.find(p => p.id === selectedProjectId);
  
  // Atualizar dados locais quando diagnostico mudar (GET)
  React.useEffect(() => {
    if (diagnostico && !diagnosticoLoading) {
      setCurrentDiagnosticoData(diagnostico);
    }
  }, [diagnostico, diagnosticoLoading]);

  const currentDiagnostico = currentDiagnosticoData;
  const currentError = diagnosticoError;
  const isLoading = diagnosticoLoading || extracoesLoading;

  // Controlar a rotação das mensagens de loading
  React.useEffect(() => {
    if (isLoading) {
      setCurrentMessageIndex(0);
      const interval = setInterval(() => {
        setCurrentMessageIndex(prev => (prev + 1) % loadingMessages.length);
      }, 2000); // Muda a mensagem a cada 2 segundos

      return () => clearInterval(interval);
    }
  }, [isLoading, loadingMessages.length]);

  // Atualizar o evento selecionado quando novos dados chegarem
  React.useEffect(() => {
    if (selectedEvent && currentDiagnostico) {
      const updatedEvent = currentDiagnostico.find(e => e.eventoId === selectedEvent.eventoId);
      if (updatedEvent) {
        setSelectedEvent(updatedEvent);
      }
    }
  }, [currentDiagnostico, selectedEvent]);

  const handleProjectSelect = async (projectId: number) => {
    setSelectedProjectId(projectId);
    setSelectedEvent(null);
    setCurrentDiagnosticoData(null); // Limpar dados antigos

    try {
      await fetchDiagnostico(projectId);
    } catch (error) {
      console.error('Erro ao buscar diagnóstico:', error);
    }
  };

  const handleEventClick = (evento: DiagnosticoEvento) => {
    setSelectedEvent(evento);
  };

  const handleBackToResults = () => {
    setSelectedEvent(null);
  };

  const handleRefreshDiagnosis = async () => {
    if (selectedProjectId) {
      setCurrentDiagnosticoData(null);
      setSelectedEvent(null);
      try {
        await fetchExtracoesEvento(selectedProjectId);
        await fetchDiagnostico(selectedProjectId);
      } catch (error) {
        console.error('Erro ao gerar diagnóstico:', error);
      }
    }
  };

  const renderResults = () => {
    if (isLoading) {
      return (
        <div className={styles.loading}>
          <Spinner size="large" />
          <Text size={400}>{loadingMessages[currentMessageIndex]}</Text>
        </div>
      );
    }

    if (currentError) {
      return (
        <div className={styles.errorCard}>
          <Title3>Erro ao carregar diagnóstico</Title3>
          <Text>
            {currentError.message || 'Ocorreu um erro ao analisar os dados do projeto.'}
          </Text>
          <Button
            appearance="primary"
            onClick={() => selectedProjectId && handleProjectSelect(selectedProjectId)}
          >
            Tentar novamente
          </Button>
        </div>
      );
    }

    if (!currentDiagnostico) {
      return null;
    }

    if (selectedEvent && selectedProjectId) {
      return (
        <ExtractionDetails
          projetoId={selectedProjectId}
          eventoId={selectedEvent.eventoId}
          eventoDescricao={selectedEvent.descricaoEvento}
          percentualAtingimento={(selectedEvent.totalPontuacaoValida / selectedEvent.totalPontuacaoMaxima) * 100}
          extracoes={selectedEvent.extracoes}
          onBack={handleBackToResults}
          onRefresh={handleRefreshDiagnosis}
        />
      );
    }

    return (
      <EventCards
        eventos={currentDiagnostico}
        onEventClick={handleEventClick}
        loading={isLoading}
      />
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Title3 className={styles.title}>Diagnóstico de uso da aplicação</Title3>
      </div>
      <div className={styles.content}>
        <div className={styles.selectorSection}>
          <ProjectSelector
            onProjectSelect={handleProjectSelect}
            onRefreshDiagnosis={handleRefreshDiagnosis}
            selectedProjectId={selectedProjectId}
            loading={isLoading}
            selectedProjectName={selectedProject?.descricao}
            loadingMessages={loadingMessages}
          />
        </div>

        <div className={styles.resultsSection}>
          {selectedProjectId ? (
            renderResults()
          ) : (
            <div className={styles.loading}>
              <Text size={400}>Selecione um projeto para visualizar o diagnóstico</Text>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Diagnostico;